import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw, Search, Download, X, ChevronDown, ChevronUp, History } from 'lucide-react';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import { formatDate } from '../../utils/dateFormat';
import { getDefaultDateRange, officialPeriodStart, officialPeriodEnd, officialPeriodRangeLabel, getLdSettings } from '../../utils/dateRange';
import { API_BASE_URL } from '../../utils/api';
import { exportSheetsToExcel } from '../../utils/excelExport';
import { useAuth } from '../../contexts/AuthContext';
import DashboardTabHeader from '../shared/DashboardTabHeader';
import { InfoTip } from '../shared/FormKit';
import ComplianceDonut from './ComplianceDonut';
import ComplianceBreakdown from './ComplianceBreakdown';
import { BANDS, BAND_BY_KEY, bandOf, progressPct, summarise } from './complianceBands';
import { exportExcelBtnCls } from '../shared/ExportExcelButton';

// Mandatory Compliance: every ACTIVE
// staff member against the yearly internal-mandatory hours target (Settings →
// L&D Training, default 40h over 1 Oct – 30 Sep). The three buckets are shown
// side by side but only Internal Mandatory is measured — non-mandatory and
// External CPD hours never offset the target. Data:
// /api/ld/mandatory-compliance, which includes staff with zero records
// (exactly who this report must not lose).
//
// The tab leads with the compliance RATE — one
// donut for the firm-wide share plus a by-group bar chart — and both redraw
// with the filters (grade / designation / department / search / period). The
// staff table sits below the charts.
//
// Two scopes, deliberately different:
//  - SCOPE (period + search + grade/designation/department) drives the donut,
//    the bars, the headline figures and the export summary.
//  - The status filter (Met / Not met) is a drill-down of those charts, so it
//    narrows the TABLE only — otherwise clicking "Not met yet" would redraw the
//    chart as 0% and destroy the very view you clicked from.

const UNSET = 'Not specified';

// The three ways this report can be sliced. `field` is the row property,
// `noun` fills the "click a bar to filter to that ___" hint.
const DIMENSIONS = [
  { key: 'grade', label: 'Grade', noun: 'grade', field: 'grade' },
  { key: 'position', label: 'Designation', noun: 'designation', field: 'position' },
  { key: 'department', label: 'Department', noun: 'department', field: 'department' },
];

const valueOf = (row, field) => row[field] || UNSET;

// One labelled fact inside an attended-session card. The label is always drawn,
// even with nothing to put under it: "Trainer —" says nobody was recorded,
// whereas a line that simply is not there leaves the reader guessing whether
// the system forgot or the session had none.
const SessionFact = ({ label, value, sub }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wider text-gray-600">{label}</dt>
    <dd className="text-sm text-gray-700 break-words">
      {value || <span className="text-gray-400">—</span>}
    </dd>
    {sub && <dd className="text-xs text-gray-600 break-all">{sub}</dd>}
  </div>
);

// `onViewJourney(staffId)` — optional: the expanded staff panel offers a jump
// to the Staff Journey tab with that person selected.
const MandatoryCompliance = ({ onViewJourney }) => {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(() => getLdSettings().mandatoryHoursTarget);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  // One entry per dimension so the bar chart can toggle whichever it groups by.
  const [dimFilters, setDimFilters] = useState({ grade: 'all', position: 'all', department: 'all' });
  const [filterBand, setFilterBand] = useState('all'); // all | met | notMet
  const [startDate, setStartDate] = useState(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().endDate);

  const [groupBy, setGroupBy] = useState('grade');
  const [sortMode, setSortMode] = useState('rate');

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/ld/mandatory-compliance?start=${startDate}&end=${endDate}`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch compliance data');
      setRows(data.data || []);
      if (data.target) setTarget(Number(data.target));
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
      console.error('Error fetching mandatory compliance:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Expanding a staff row lists the mandatory sessions behind their hours,
  // the same gesture Training Sessions and the External CPD
  // participant summary use. Fetched on open — the report is one row per active
  // employee, so loading everyone's sessions up front would be most of the
  // training history for a panel nobody may open.
  const [expandedStaffId, setExpandedStaffId] = useState(null);
  const [staffSessions, setStaffSessions] = useState(null); // { loading, error, data }
  const openRowRef = useRef(null);

  // The staff table is nine columns wide and scrolls sideways on anything
  // narrower than ~1550px. The panel lives in a cell spanning all nine, so it
  // inherits the full table width and its right-hand half — the Close button,
  // the hours total — sits off-screen until you scroll. Pinning it to the left
  // edge of the scroll container at exactly the container's visible width keeps
  // the whole panel readable while the table underneath still scrolls. On a
  // wide screen the two widths are equal and nothing moves.
  // A callback ref, not useEffect + [] — the table is not in the tree on the
  // first render (the loading branch returns early), so a mount-time effect
  // measures nothing and never runs again.
  const [panelWidth, setPanelWidth] = useState(null);
  const wrapObserverRef = useRef(null);
  const attachTableWrap = useCallback((el) => {
    if (wrapObserverRef.current) {
      wrapObserverRef.current.disconnect();
      wrapObserverRef.current = null;
    }
    if (!el || typeof ResizeObserver === 'undefined') return;
    setPanelWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setPanelWidth(el.clientWidth));
    ro.observe(el);
    wrapObserverRef.current = ro;
  }, []);

  const collapseStaff = () => {
    const row = openRowRef.current;
    const offscreen = row ? row.getBoundingClientRect().top < Math.max(0, row.closest('.table-scroll')?.getBoundingClientRect().top ?? 0) : false;
    setExpandedStaffId(null);
    setStaffSessions(null);
    if (offscreen) {
      requestAnimationFrame(() => row.scrollIntoView({ block: 'center', behavior: 'smooth' }));
    }
  };

  const toggleStaff = async (staffId) => {
    if (expandedStaffId === staffId) { collapseStaff(); return; }
    setExpandedStaffId(staffId);
    setStaffSessions({ loading: true, error: null, data: null });
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/ld/mandatory-compliance/${staffId}/sessions?start=${startDate}&end=${endDate}`,
        { credentials: 'include' }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to load mandatory sessions');
      setStaffSessions({ loading: false, error: null, data: body.data || [] });
    } catch (e) {
      setStaffSessions({ loading: false, error: e.message, data: null });
    }
  };

  // A panel fetched for one period must not survive into another — collapse
  // whenever the period moves, rather than leaving stale sessions under a row
  // whose hours have just been recalculated.
  useEffect(() => { setExpandedStaffId(null); setStaffSessions(null); }, [startDate, endDate]);

  // Esc closes the open staff row, the same key that closes every panel here.
  useEffect(() => {
    if (expandedStaffId == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') collapseStaff(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedStaffId]);

  const withBand = useMemo(
    () => rows.map(r => ({
      ...r,
      totalHours: r.mandatoryHours + r.nonMandatoryHours + r.externalHours,
      gap: Math.max(0, target - r.mandatoryHours),
      band: bandOf(r.mandatoryHours, target),
      pct: progressPct(r.mandatoryHours, target),
      met: target > 0 && r.mandatoryHours >= target,
    })),
    [rows, target]
  );

  // Filter options come from the full row set, so a dimension never loses its
  // own options the moment you pick one.
  const optionsFor = useCallback((field) => (
    [...new Set(withBand.map(r => valueOf(r, field)))].sort((a, b) => a.localeCompare(b))
  ), [withBand]);

  const matchesSearch = useCallback((r) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
  }, [searchTerm]);

  // `skipDim` leaves one dimension unfiltered — the bar chart uses it so that
  // clicking a bar highlights that group while its peers stay on screen to
  // compare against.
  const inScope = useCallback((r, skipDim = null) => {
    if (!matchesSearch(r)) return false;
    return DIMENSIONS.every(d => (
      d.key === skipDim || dimFilters[d.key] === 'all' || valueOf(r, d.field) === dimFilters[d.key]
    ));
  }, [matchesSearch, dimFilters]);

  const scopeRows = useMemo(() => withBand.filter(r => inScope(r)), [withBand, inScope]);
  const tableRows = useMemo(
    () => scopeRows.filter(r => filterBand === 'all' || r.band === filterBand),
    [scopeRows, filterBand]
  );

  const summary = useMemo(() => summarise(scopeRows), [scopeRows]);

  const dimension = DIMENSIONS.find(d => d.key === groupBy);
  const groups = useMemo(() => {
    const source = withBand.filter(r => inScope(r, groupBy));
    const buckets = new Map();
    source.forEach(r => {
      const value = valueOf(r, dimension.field);
      if (!buckets.has(value)) buckets.set(value, []);
      buckets.get(value).push(r);
    });
    const list = [...buckets.entries()].map(([value, members]) => ({
      value, label: value, ...summarise(members),
    }));
    // Lowest compliance first by default — the groups still needing chasing
    // belong at the top; ties break on the bigger headcount, then name.
    return list.sort((a, b) => (
      sortMode === 'name'
        ? a.label.localeCompare(b.label)
        : a.rate - b.rate || b.total - a.total || a.label.localeCompare(b.label)
    ));
  }, [withBand, inScope, groupBy, dimension, sortMode]);

  const toggleDim = (key, value) => setDimFilters(prev => ({
    ...prev, [key]: prev[key] === value ? 'all' : value,
  }));
  const setDim = (key, value) => setDimFilters(prev => ({ ...prev, [key]: value }));

  const activeChips = [
    ...DIMENSIONS
      .filter(d => dimFilters[d.key] !== 'all')
      .map(d => ({ key: d.key, label: `${d.label}: ${dimFilters[d.key]}`, clear: () => setDim(d.key, 'all') })),
    ...(filterBand !== 'all'
      ? [{ key: 'band', label: `Status: ${BAND_BY_KEY[filterBand].label}`, clear: () => setFilterBand('all') }]
      : []),
    ...(searchTerm ? [{ key: 'search', label: `Search: ${searchTerm}`, clear: () => setSearchTerm('') }] : []),
  ];
  const clearAll = () => {
    setSearchTerm('');
    setDimFilters({ grade: 'all', position: 'all', department: 'all' });
    setFilterBand('all');
  };

  // Sorted + paged PAGE_SIZE at a time, the house rule for every table
  // — shared hook so this page cannot drift from the others.
  const {
    rows: paginated, sortConfig, requestSort, page, setPage, total,
  } = usePagedSortableData(
    tableRows, { key: 'mandatoryHours', direction: 'asc', accessor: r => r.mandatoryHours }
  );

  const periodLabel = `${formatDate(startDate)} – ${formatDate(endDate)}`;
  const scopeLabel = activeChips.length === 0
    ? 'All active staff'
    : activeChips.map(c => c.label).join(' · ');

  const handleExport = () => {
    const summarySheet = [
      { Metric: 'Period', Value: periodLabel },
      { Metric: 'Filters', Value: scopeLabel },
      { Metric: 'Target (hours per person)', Value: target },
      { Metric: 'Staff in scope', Value: summary.total },
      { Metric: 'Met target', Value: summary.counts.met },
      { Metric: 'Not met yet', Value: summary.counts.notMet },
      { Metric: 'Compliance rate (%)', Value: Number(summary.rate.toFixed(1)) },
      { Metric: 'Average mandatory hours', Value: Number(summary.avgHours.toFixed(1)) },
    ];
    const groupSheet = groups.map(g => ({
      [dimension.label]: g.label,
      'Staff': g.total,
      'Met target': g.counts.met,
      'Not met yet': g.counts.notMet,
      'Compliance rate (%)': Number(g.rate.toFixed(1)),
      'Average mandatory hours': Number(g.avgHours.toFixed(1)),
    }));
    const staffSheet = tableRows.map(r => ({
      'Period': periodLabel,
      'Name': r.name,
      'Email': r.email,
      'Designation': r.position || '',
      'Grade': r.grade || '',
      'Department': r.department || '',
      'Internal Mandatory Hours': Number(r.mandatoryHours.toFixed(1)),
      'Target': target,
      '% of Target': Number(r.pct.toFixed(0)),
      'Gap': Number(r.gap.toFixed(1)),
      'Status': BAND_BY_KEY[r.band].label,
      'Internal Non-mandatory Hours': Number(r.nonMandatoryHours.toFixed(1)),
      'External CPD Hours': Number(r.externalHours.toFixed(1)),
    }));
    exportSheetsToExcel([
      { name: 'Summary', rows: summarySheet },
      { name: `By ${dimension.label}`, rows: groupSheet },
      { name: 'Staff Compliance', rows: staffSheet },
    ], 'Mandatory_Compliance.xlsx');
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600">Loading mandatory compliance...</p>
        </div>
      </div>
    );
  }

  if (error && rows.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="text-red-800">
          <h3 className="font-semibold">Error loading mandatory compliance</h3>
          <p className="text-sm mt-1">{error}</p>
        </div>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description={
          <>
            Every active staff member against the {target}-hour internal mandatory target.
            Non-mandatory and External CPD hours are shown for reference only — they never count towards the target.
          </>
        }
        lastUpdated={lastUpdated}
        actions={
          <>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {hasPermission('ld:view') && (
              <button
                onClick={handleExport}
                disabled={scopeRows.length === 0}
                className={exportExcelBtnCls}
                title="Summary, the current breakdown, and the staff list — all as filtered"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            )}
          </>
        }
      />

      {/* Period */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">
            Period
          </span>
          <label className="text-sm text-gray-600">From</label>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <label className="text-sm text-gray-600">To</label>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={() => {
              setStartDate(officialPeriodStart());
              setEndDate(officialPeriodEnd());
            }}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Reset to Default ({officialPeriodRangeLabel()})
          </button>
        </div>
      </div>

      {/* Filters — one row above the charts, because everything below reacts to
          them (the rate must follow grade / department). */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {DIMENSIONS.map(d => (
            <div key={d.key}>
              <select
                value={dimFilters[d.key]}
                onChange={(e) => setDim(d.key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700"
                aria-label={`Filter by ${d.label}`}
              >
                <option value="all">All {d.label === 'Grade' ? 'Grades' : `${d.label}s`}</option>
                {optionsFor(d.field).map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">
              {summary.total} of {withBand.length} active staff in scope
              {filterBand !== 'all' && ` · table shows ${tableRows.length} (${BAND_BY_KEY[filterBand].label})`}
            </span>
            {activeChips.map(chip => (
              <button
                key={chip.key}
                onClick={chip.clear}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-800 border border-blue-200 hover:bg-blue-100 transition-colors"
              >
                {chip.label}
                <X className="w-3 h-3" />
              </button>
            ))}
          </div>
          {activeChips.length > 0 && (
            <button onClick={clearAll} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Charts — both scoped to the filters above */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ComplianceDonut
          summary={summary}
          target={target}
          activeBand={filterBand === 'all' ? null : filterBand}
          onBandToggle={(key) => setFilterBand(prev => (prev === key ? 'all' : key))}
        />
        <div className="lg:col-span-2">
          <ComplianceBreakdown
            dimensions={DIMENSIONS}
            groupBy={groupBy}
            onGroupBy={setGroupBy}
            groups={groups}
            sortMode={sortMode}
            onSortMode={setSortMode}
            activeValue={dimFilters[groupBy] === 'all' ? null : dimFilters[groupBy]}
            onGroupToggle={(value) => toggleDim(groupBy, value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-1.5">
            Staff Compliance ({tableRows.length})
            <InfoTip text={`The target and training year are set under Management → L&D Setup → Training Settings. Status bands: ${BANDS.map(b => b.label).join(' · ')} — only internal mandatory hours count towards the target.`} />
          </h3>
          <p className="text-sm text-gray-500">{periodLabel}</p>
        </div>
        <div className="table-scroll" ref={attachTableWrap}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Staff" sortKey="name" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Designation" sortKey="position" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Department" sortKey="department" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label={`Mandatory (of ${target})`} sortKey="mandatoryHours" sortConfig={sortConfig} onSort={requestSort} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Gap" sortKey="gap" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Status" sortKey="band" sortConfig={sortConfig} onSort={requestSort} accessor={r => r.pct} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="Non-mandatory" sortKey="nonMandatoryHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="External CPD" sortKey="externalHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
                <SortableHeader label="" sortKey="" sortable={false} className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">No staff match the current filters</td>
                </tr>
              ) : paginated.map((r) => {
                const band = BAND_BY_KEY[r.band];
                const isOpen = expandedStaffId === r.staffId;
                return (
                  <React.Fragment key={r.staffId}>
                  <tr
                    ref={isOpen ? openRowRef : null}
                    className={`cursor-pointer transition-colors ${isOpen ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                    onClick={() => toggleStaff(r.staffId)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleStaff(r.staffId); }
                    }}
                  >
                    <td className={`px-6 py-4 whitespace-nowrap border-l-4 ${isOpen ? 'border-blue-600' : 'border-transparent'}`}>
                      <div className="text-sm font-medium text-gray-900">{r.name}</div>
                      <div className="text-sm text-gray-500">{r.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{r.position || '—'}</div>
                      <div className="text-xs text-gray-600">{r.grade || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{r.department || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-semibold w-14 text-right tabular-nums ${r.met ? 'text-emerald-700' : 'text-gray-900'}`}>
                          {r.mandatoryHours.toFixed(1)}
                        </span>
                        <div className="w-28 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${band.bar}`} style={{ width: `${r.pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-10 tabular-nums">{r.pct.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium tabular-nums">
                      {r.gap > 0 ? (
                        <span className="text-amber-700">{r.gap.toFixed(1)}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs leading-5 font-semibold rounded-full border ${band.badge}`}>
                        {band.short}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-indigo-600 tabular-nums">
                      {r.nonMandatoryHours.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 tabular-nums">
                      {r.externalHours.toFixed(1)}
                    </td>
                    {/* A worded control, not a bare chevron — a lone chevron
                        reads as decoration. */}
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors ${
                          isOpen
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'bg-white border-gray-300 text-gray-600'
                        }`}
                      >
                        {isOpen ? (
                          <>Close <ChevronUp className="w-4 h-4" /></>
                        ) : (
                          <>Courses <ChevronDown className="w-4 h-4" /></>
                        )}
                      </span>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan="9" className="p-0 bg-blue-50/40 border-l-4 border-blue-600">
                        <div
                          className="sticky left-0 px-6 py-4 space-y-4"
                          style={panelWidth ? { width: panelWidth } : undefined}
                        >
                          {/* Name and period as one middot-separated line would
                              read as a single run-on string: a heading plus a
                              labelled pill instead. No hours-counted pill
                              — the row above and the footer already say it. */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Mandatory Courses Attended</p>
                              <h4 className="mt-1 text-base font-semibold text-gray-900 truncate">{r.name}</h4>
                              <div className="mt-2 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-white px-3 py-1 text-xs text-gray-600">
                                  {periodLabel}
                                </span>
                              </div>
                            </div>
                            <div className="shrink-0 flex items-center gap-2">
                              {onViewJourney && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); onViewJourney(r.staffId); }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-300 bg-white text-sm text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors"
                                  title="Open this person's full learning history on the Staff Journey tab"
                                >
                                  <History className="w-4 h-4" /> Learning Journey
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); collapseStaff(); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-colors"
                              >
                                <X className="w-4 h-4" /> Close
                                <span className="text-xs text-gray-500">Esc</span>
                              </button>
                            </div>
                          </div>

                          {staffSessions?.loading && (
                            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                              <RefreshCw className="w-4 h-4 animate-spin" /> Loading mandatory courses…
                            </div>
                          )}
                          {staffSessions?.error && (
                            <div className="text-sm text-red-600 py-2">{staffSessions.error}</div>
                          )}
                          {staffSessions?.data && (
                            staffSessions.data.length === 0 ? (
                              <p className="text-sm text-gray-500 py-2">
                                No internal mandatory sessions attended in this period — the whole {target}-hour target is still outstanding.
                              </p>
                            ) : (
                              /* Cards, not a table. Course title, trainer,
                                 hours, time and
                                 venue are five facts per session — as columns
                                 inside an already 9-column table they would be
                                 unreadable slivers. Each card gives the title a
                                 line of its own and lays the rest out as
                                 labelled facts, two cards abreast on a wide
                                 screen, one on a narrow one. */
                              <>
                                <ul className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                                  {staffSessions.data.map((s) => (
                                    <li key={s.sessionId} className="rounded-xl border border-blue-200 bg-white p-4 shadow-sm">
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            {s.categoryTitle || 'Mandatory'}
                                          </span>
                                          {/* Did not attend; passed the quiz — the hours are credited (ld.md §5.7). */}
                                          {s.creditSource === 'quiz' && (
                                            <span className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-700" title="Did not attend; passed the post-course quiz, so the session's hours are credited">
                                              Credited via quiz
                                            </span>
                                          )}
                                          {/* Full title, wrapped — truncating
                                              the one thing the reader came for
                                              defeats the panel. */}
                                          <h5 className="mt-2 text-sm font-semibold text-gray-900 leading-snug break-words">
                                            {s.courseTitle || s.sessionCode}
                                          </h5>
                                        </div>
                                        <div className="shrink-0 text-right">
                                          <div className="text-2xl font-semibold text-gray-900 tabular-nums leading-none">
                                            {s.hours.toFixed(1)}
                                          </div>
                                          <div className="mt-1 text-[11px] uppercase tracking-wider text-gray-600">hours</div>
                                        </div>
                                      </div>
                                      <dl className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                        <SessionFact
                                          label="When"
                                          value={`${formatDate(s.date)}${s.startEndTime ? ` · ${s.startEndTime}` : ''}`}
                                        />
                                        <SessionFact label="Venue" value={s.venueName} />
                                        <SessionFact label="Trainer" value={s.trainerNames} />
                                        <SessionFact label="Session ID" value={s.sessionCode} />
                                      </dl>
                                    </li>
                                  ))}
                                </ul>

                                {/* The total is the point: it must equal the
                                    Mandatory figure on the row above, so it is
                                    stated next to what is still outstanding. */}
                                <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-blue-200 bg-white px-4 py-3">
                                  <span className="text-sm text-gray-600">
                                    {staffSessions.data.length} session{staffSessions.data.length === 1 ? '' : 's'} counted towards the {target}-hour target
                                  </span>
                                  <span className="text-sm text-gray-700">
                                    <span className="text-lg font-semibold text-gray-900 tabular-nums">
                                      {staffSessions.data.reduce((sum, s) => sum + s.hours, 0).toFixed(1)}
                                    </span>{' '}
                                    hrs
                                    {r.gap > 0 && (
                                      <span className="ml-3 text-amber-700 font-medium">{r.gap.toFixed(1)} hrs still to go</span>
                                    )}
                                  </span>
                                </div>
                              </>
                            )
                          )}

                          {/* Second way out, for a staff member with a long list. */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); collapseStaff(); }}
                            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-blue-300 bg-white/60 text-sm font-medium text-blue-700 hover:bg-blue-50 hover:border-blue-400 transition-colors"
                          >
                            <ChevronUp className="w-4 h-4" /> Collapse This Staff Member
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TablePager page={page} setPage={setPage} total={total} />
        <p className="text-xs text-gray-500 mt-3">
          Hours are attributed by session date (internal) and certificate date (External CPD).
        </p>
      </div>
    </div>
  );
};

export default MandatoryCompliance;
